/**
 * SensorModalClassic — Monolithic environmental sensor modal template.
 *
 * Read-only sensor display panel:
 * - Readings: temperature, humidity, CO2, PM2.5, TVOC, lux
 * - History: placeholder for future chart integration
 *
 * No commands — purely observational.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './SensorModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const SENSOR_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Readings Section ──
  { id: 'readings', label: 'Sensor Readings', icon: 'sensors', slots: [
    { id: 'readings-label', label: 'Title "READINGS"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'sensor-card', label: 'Reading Card Container', editableProps: ['background', 'borderRadius', 'padding', 'border', 'gap'] },
    { id: 'sensor-card-icon', label: 'Reading Card Icon', editableProps: ['fontSize', 'color'] },
    { id: 'sensor-card-name', label: 'Reading Card Label', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'sensor-card-value', label: 'Reading Card Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'sensor-card-unit', label: 'Reading Card Unit', editableProps: ['fontSize', 'color'] },
  ]},
  // ── History Section ──
  { id: 'history', label: 'History', icon: 'timeline', slots: [
    { id: 'history-label', label: 'Title "HISTORY"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'history-placeholder', label: 'Placeholder Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
]

const DEFAULT_SECTION_ORDER = SENSOR_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Sensor card config
// ---------------------------------------------------------------------------

type SensorCardDef = {
  keys: string[]
  label: string
  icon: string
  unit: string
  decimals: number
  /** Optional color override for icon based on value level */
  colorFn?: (val: number) => string
}

const SENSOR_CARDS: SensorCardDef[] = [
  {
    keys: ['temperatureAmbientCelsius', 'temperature', 'currentTemperature', 'ambientTemperature'],
    label: 'Temperature',
    icon: 'thermostat',
    unit: '°C',
    decimals: 1,
    colorFn: (v) => v > 30 ? '#f97316' : v < 18 ? '#60a5fa' : '#22d3ee',
  },
  {
    keys: ['humidityAmbientPercent', 'humidity', 'relativeHumidity'],
    label: 'Humidity',
    icon: 'humidity_percentage',
    unit: '%',
    decimals: 0,
    colorFn: (v) => v > 70 ? '#818cf8' : v < 30 ? '#fb923c' : '#34d399',
  },
  {
    keys: ['co2Level', 'co2', 'carbonDioxidePpm'],
    label: 'CO₂',
    icon: 'co2',
    unit: 'ppm',
    decimals: 0,
    colorFn: (v) => v > 2000 ? '#ef4444' : v > 1000 ? '#f97316' : '#34d399',
  },
  {
    keys: ['pm25Level', 'pm25', 'pm2_5'],
    label: 'PM2.5',
    icon: 'air',
    unit: 'μg/m³',
    decimals: 1,
    colorFn: (v) => v > 55 ? '#ef4444' : v > 15 ? '#f97316' : '#34d399',
  },
  {
    keys: ['tvocLevel', 'tvoc', 'totalVolatileOrganicCompoundsPartsPerMillion'],
    label: 'TVOC',
    icon: 'science',
    unit: 'ppb',
    decimals: 0,
    colorFn: (v) => v > 2200 ? '#ef4444' : v > 660 ? '#f97316' : '#34d399',
  },
  {
    keys: ['luxLevel', 'lux', 'illuminanceLux'],
    label: 'Lux',
    icon: 'light_mode',
    unit: 'lux',
    decimals: 0,
    colorFn: () => '#facc15',
  },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SensorModalClassic({
  device,
  deviceState,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- Build visible sensor cards ----
  const visibleCards = useMemo(() => {
    return SENSOR_CARDS.map((card) => {
      const value = numAttr(deviceState, ...card.keys)
      return { ...card, value }
    }).filter((card) => card.value !== null)
  }, [deviceState])

  return (
    <div
      className={`sensor-modal-classic${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="sensor-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="sensor-modal-classic__header">
        <button className="sensor-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="sensor-modal-classic__title" data-modal-slot="header-title">{device.name || 'Room Sensor'}</h2>
        <button
          className="sensor-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'readings':
            return (
              <div key="readings" className={`sensor-modal-classic__readings${hl}`} data-modal-section="readings">
                <div className="sensor-modal-classic__section-label" data-modal-slot="readings-label">READINGS</div>
                {(visibleCards.length > 0 || isEditorMode) ? (
                  <div className="sensor-modal-classic__cards-grid">
                    {/* In editor mode with no state, show placeholder cards */}
                    {(isEditorMode && visibleCards.length === 0 ? SENSOR_CARDS.slice(0, 4).map((c) => ({ ...c, value: 0 })) : visibleCards).map((card) => (
                      <div
                        key={card.keys[0]}
                        className="sensor-modal-classic__card"
                        data-modal-slot="sensor-card"
                      >
                        <span
                          className="material-symbols-rounded sensor-modal-classic__card-icon"
                          data-modal-slot="sensor-card-icon"
                          style={card.colorFn ? { color: card.colorFn(card.value ?? 0) } : undefined}
                        >
                          {card.icon}
                        </span>
                        <div className="sensor-modal-classic__card-body">
                          <span className="sensor-modal-classic__card-name" data-modal-slot="sensor-card-name">
                            {card.label}
                          </span>
                          <div className="sensor-modal-classic__card-reading">
                            <span className="sensor-modal-classic__card-value" data-modal-slot="sensor-card-value">
                              {card.value !== null ? card.value!.toFixed(card.decimals) : '—'}
                            </span>
                            <span className="sensor-modal-classic__card-unit" data-modal-slot="sensor-card-unit">
                              {card.unit}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="sensor-modal-classic__empty">No sensor data available</div>
                )}
              </div>
            )
          case 'history':
            return (
              <div key="history" className={`sensor-modal-classic__history${hl}`} data-modal-section="history">
                <div className="sensor-modal-classic__section-label" data-modal-slot="history-label">HISTORY</div>
                <div className="sensor-modal-classic__history-placeholder" data-modal-slot="history-placeholder">
                  <span className="material-symbols-rounded sensor-modal-classic__history-icon">timeline</span>
                  <span>No chart data</span>
                </div>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
